import type { DimSumEnhancedNode, RootNodesForDimensionsResponse } from '@amzn/gft-dimsum-service';
import { DEFAULT_PAGE_DRIVERS, KPI_DIMENSION_RENDER_LIST } from '@constants/page';
import { KPIDrivers, KPIDriversInput } from '@services/api/deckManagement/graphql/schema/API';
import { isNil } from 'lodash';
import { PartialKPIDrivers } from 'types/Page.types';
import { DriverDisplayOptionMap } from 'types/PageContext.types';
import { BaseKpiDimensionFieldTypes, KpiDimension, KpiDimensionConfig } from 'types/PageSidePanel.types';

/** A KPIDrivers object with all fields set to null. Used when initializing empty deckline drivers. */
export const EMPTY_KPI_DRIVERS: KPIDrivers = {
  __typename: 'KPIDrivers',
  account_hierarchy: null,
  account: null,
  location_hierarchy: null,
  location: null,
  project_hierarchy: null,
  project: null,
  company_hierarchy: null,
  company: null,
  product_hierarchy: null,
  product: null,
  revgp_hierarchy: null,
  revgp: null,
  datasource_hierarchy: null,
  datasource: null,
  channel_hierarchy: null,
  channel: null,
  costcenter_hierarchy: null,
  costcenter: null,
  costtype: null,
  costpool: null,
};

/** A KPIDriversInput object with all fields set to null. Used when initializing empty variant overrides. */
export const EMPTY_KPI_DRIVERS_INPUT: KPIDriversInput = {
  account_hierarchy: null,
  account: null,
  location_hierarchy: null,
  location: null,
  project_hierarchy: null,
  project: null,
  company_hierarchy: null,
  company: null,
  product_hierarchy: null,
  product: null,
  revgp_hierarchy: null,
  revgp: null,
  datasource_hierarchy: null,
  datasource: null,
  channel_hierarchy: null,
  channel: null,
  costcenter_hierarchy: null,
  costcenter: null,
  costtype: null,
  costpool: null,
};

/**
 * Sanitizes KPI Drivers by trimming string values
 * @param kpiDrivers - The KPI Drivers to sanitize
 * @returns The sanitized KPI Drivers
 */
export const sanitizeKPIDrivers = (kpiDrivers: KPIDrivers | null | undefined): KPIDrivers | null | undefined => {
  if (!kpiDrivers) {
    return kpiDrivers;
  }

  // Trim all string fields
  return Object.fromEntries(
    Object.entries(kpiDrivers).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value]),
  ) as KPIDrivers;
};

/**
 * Produces the "effective" drivers for a deckline by merging page-level drivers with deckline-level overrides.
 * Null values in deckline drivers are treated as "inherit from page." Non-null deckline values take precedence.
 *
 * @param decklineDrivers - The deckline's sparse driver overrides (null values = inherit)
 * @param pageDrivers - The page-level default drivers
 * @returns The merged effective drivers, or null if neither source is available
 */
export const getEffectiveDrivers = (
  decklineDrivers: KPIDrivers | null | undefined,
  pageDrivers: KPIDrivers | null | undefined,
): KPIDrivers | null => {
  if (!pageDrivers) return decklineDrivers ?? null;
  if (!decklineDrivers) return pageDrivers;

  return Object.keys(pageDrivers).reduce((acc, key) => {
    if (key === '__typename') {
      acc[key] = pageDrivers[key];
    } else {
      const k = key as keyof Omit<KPIDrivers, '__typename'>;
      (acc as Record<string, unknown>)[k] = decklineDrivers[k] ?? pageDrivers[k];
    }
    return acc;
  }, {} as KPIDrivers);
};

/**
 * Checks if a KPI Driver is empty, which can be true when the kpiDrivers object is null, or when every value within it is nil.
 * @param kpiDrivers
 */
export const kpiDriversEmpty = (kpiDrivers?: KPIDrivers | null) => {
  if (isNil(kpiDrivers)) {
    return true;
  }

  // Check if every value in kpiDrivers (except the `__typename` property) is nil
  return Object.keys(kpiDrivers)
    .filter((key) => key !== '__typename')
    .every((key) => isNil(kpiDrivers[key as keyof KPIDrivers]));
};

/**
 * Generates Driver Display Options.
 *
 * NOTE:
 *   * When `parentKpiDrivers` is empty, then `kpiDrivers` is referring to the Page Drivers.
 *   * When `parentKpiDrivers` is not empty, then `kpiDrivers` is referring to the Deckline Drivers, and `parentKpiDrivers` is referring to the Page Drivers.
 * @param kpiDrivers - The KPI drivers to generate display options for
 * @param parentKpiDrivers - Optional parent KPI drivers for comparison
 * @param alwaysActive - List of KpiDimensions to force as active regardless of comparison logic
 */
export const generateDriverDisplayOptions = (
  kpiDrivers?: KPIDrivers | null,
  parentKpiDrivers?: KPIDrivers | null,
  alwaysActive?: KpiDimension[],
) => {
  const result: DriverDisplayOptionMap = {};
  if (!isNil(kpiDrivers)) {
    KPI_DIMENSION_RENDER_LIST.forEach((kpiConfig) => {
      const { decklineDrivers, key, baseKpiField } = kpiConfig;
      const kpiDimension = key as KpiDimension;

      if (kpiDimension) {
        if (baseKpiField.type === BaseKpiDimensionFieldTypes.ReadOnly && !isNil(baseKpiField.staticValue)) {
          result[kpiDimension] = {
            kpiDimension,
            value: baseKpiField.staticValue,
            // For static readonly values, `active` is TRUE for pageDrivers, and FALSE for Deckline Drivers.
            // (`parentKpiDrivers` will be undefined/null when we're processing Page Drivers through this function)
            active: isNil(parentKpiDrivers),
          };
        } else if (decklineDrivers) {
          const { valueAccessor, hierarchyAccessor } = decklineDrivers;
          const driverValue = kpiDrivers[valueAccessor];
          const hierarchyValue = hierarchyAccessor ? kpiDrivers[hierarchyAccessor] : null;
          const parentDriverValue = parentKpiDrivers?.[valueAccessor];
          const parentHierarchyValue = hierarchyAccessor ? parentKpiDrivers?.[hierarchyAccessor] : null;
          let activeState: boolean;
          if (alwaysActive?.includes(kpiDimension)) {
            activeState = true;
          } else if (isNil(driverValue) && !isNil(parentDriverValue)) {
            // Null deckline value with a non-null page value = inherited from page (cascading) = not active
            activeState = false;
          } else if (isNil(driverValue) && isNil(parentDriverValue)) {
            // Both null = no value to inherit, user needs to provide one = active
            activeState = true;
          } else {
            activeState = driverValue !== parentDriverValue || hierarchyValue !== parentHierarchyValue;
          }

          result[kpiDimension] = {
            kpiDimension,
            // When value is null (cascading), display the inherited parent value
            hierarchy: hierarchyValue ?? parentHierarchyValue ?? '',
            value: driverValue ?? parentDriverValue ?? '',
            active: activeState,
          };
        }
      }
    });
  }
  return result;
};

/**
 * Generates driver display options for denominator drivers with account dimension always active.
 * The account dimension is forced active to ensure it's always editable in denominator context.
 * @param denominatorDrivers - The denominator KPI drivers
 * @param pageDrivers - The page-level KPI drivers for comparison
 * @returns Driver display options with account dimension marked as active
 */
export const generateDenominatorDriverDisplayOptions = (
  denominatorDrivers: KPIDrivers,
  pageDrivers: KPIDrivers,
): ReturnType<typeof generateDriverDisplayOptions> => {
  // Re-uses generateDriverDisplayOptions() and always ensures that the `account` dimension is active/editable.
  return generateDriverDisplayOptions(denominatorDrivers, pageDrivers, [KpiDimension.ACCOUNT]);
};

/**
 * Generates a base KPI ID for the given segment data
 * @param segmentDisplayData
 * @param rootNodes - Needed for determining if a segment's node is a Root Node or not.
 */
export const generateBaseKpiId = (
  segmentDisplayData: DriverDisplayOptionMap,
  rootNodes: RootNodesForDimensionsResponse,
) => {
  // Determines this KPI Dimensions need to be included in the KPI ID. Return `null` when a dimension isn't needed.
  const targets = KPI_DIMENSION_RENDER_LIST.map((config) => {
    const { key, formulaKey, baseKpiField } = config;

    // Dimension Picker Fields
    if (baseKpiField.type === BaseKpiDimensionFieldTypes.DimensionPicker) {
      const segmentHierarchy = segmentDisplayData[key]?.hierarchy;
      const segmentValue = segmentDisplayData[key]?.value;
      const dimSumNodeId = [segmentHierarchy, segmentValue].join('-');
      const isRootNode = rootNodes?.[key]?.some((node) => {
        return node?.nodeID === dimSumNodeId;
      });

      // We need to include the dimension with the node is NOT a Root Node.
      if (!isRootNode) {
        return `${formulaKey}:${segmentValue}`;
      }

      // Don't include this node for generating the KPI ID.
      return null;
    }

    // Select Fields
    if (baseKpiField.type === BaseKpiDimensionFieldTypes.Select) {
      const segmentValue = segmentDisplayData[key]?.value;

      if (segmentValue !== baseKpiField.rootValue) {
        return `${formulaKey}:${segmentValue}`;
      }

      // Don't include this node for generating the KPI ID.
      return null;
    }

    // Unhandled Field Types
    return null;
  }).filter((target) => !isNil(target));

  // Construct the KPI ID string
  return `|${targets.join('|')}|`;
};

/**
 * Checks if deckline drivers match page KPI drivers for all non-null page driver values
 * @param decklineDrivers - The deckline's KPI drivers
 * @param pageDrivers - The page's KPI drivers
 * @returns true if all non-null page driver values match deckline values, false otherwise
 */
export const hasDriverMismatch = (decklineDrivers?: KPIDrivers | null, pageDrivers?: KPIDrivers | null): boolean => {
  if (!decklineDrivers || !pageDrivers) return false;

  return Object.keys(pageDrivers).some((key) => {
    if (key === '__typename') return false;
    const k = key as keyof Omit<KPIDrivers, '__typename'>;
    const decklineValue = decklineDrivers[k];
    if (isNil(decklineValue)) return false; // null = inherited from page = not a mismatch
    const pageValue = pageDrivers[k];
    if (isNil(pageValue)) return false;
    if (pageValue === DEFAULT_PAGE_DRIVERS[k]) return false; // page at default = not an exception
    return pageValue !== decklineValue;
  });
};

/**
 * Returns only the KPIDrivers keys whose values differ between two driver objects.
 * Companion to {@link hasDriverMismatch} — use this when you need the actual changed values.
 *
 * @param original - The original driver values
 * @param current - The current driver values
 * @returns An object containing only the keys/values that changed
 */
export const diffKPIDrivers = (original: KPIDrivers, current: KPIDrivers): PartialKPIDrivers =>
  Object.keys(current).reduce((acc, key) => {
    if (key !== '__typename' && current[key as keyof KPIDrivers] !== original[key as keyof KPIDrivers]) {
      acc[key as keyof PartialKPIDrivers] = current[key as keyof KPIDrivers];
    }
    return acc;
  }, {} as PartialKPIDrivers);

/**
 * Converts KPI driver values (hierarchy + dimension value) into a DimSum node ID string.
 * The node ID format is `{hierarchyValue}-{dimensionValue}`.
 *
 * @param kpiDrivers - The KPI driver values to convert
 * @param kpiConfig - The KPI dimension configuration containing accessor mappings
 * @returns The composed DimSum node ID, or null if required values are missing
 */
export const convertKpiDriverValuesToDimSumNodeId = (
  kpiDrivers: Partial<KPIDrivers> | null,
  kpiConfig: KpiDimensionConfig<KpiDimension>,
): string | null => {
  if (isNil(kpiDrivers)) {
    return null;
  }

  if (kpiConfig.baseKpiField.type !== BaseKpiDimensionFieldTypes.DimensionPicker) {
    return null;
  }

  const { decklineDrivers } = kpiConfig;

  if (isNil(decklineDrivers)) {
    return null;
  }

  const { valueAccessor, hierarchyAccessor } = decklineDrivers;

  if (isNil(valueAccessor) || isNil(hierarchyAccessor)) {
    return null;
  }

  const hierarchyValue = kpiDrivers[hierarchyAccessor];
  const dimensionValue = kpiDrivers[valueAccessor];

  if (!hierarchyValue || !dimensionValue) {
    return null;
  }

  return [hierarchyValue, dimensionValue].join('-');
};

/**
 * Converts a DimSum node selection into KPI driver key-value pairs.
 * Splits the node ID on the first `-` to extract the hierarchy and dimension values.
 * Returns nullified fields when the node is null (clearing the selection).
 *
 * @param node - The selected DimSum node, or null to clear the selection
 * @param kpiConfig - The KPI dimension configuration containing accessor mappings
 * @returns Partial KPIDrivers object with the hierarchy and value fields set
 */
export const convertDimSumNodeToKpiDriverValues = (
  node: DimSumEnhancedNode | null,
  kpiConfig: KpiDimensionConfig<KpiDimension>,
): Partial<KPIDrivers> => {
  if (kpiConfig.baseKpiField.type !== BaseKpiDimensionFieldTypes.DimensionPicker) {
    return {};
  }

  const { decklineDrivers } = kpiConfig;

  if (isNil(decklineDrivers)) {
    return {};
  }

  const { valueAccessor, hierarchyAccessor } = decklineDrivers;

  if (isNil(valueAccessor) || isNil(hierarchyAccessor)) {
    return {};
  }

  if (isNil(node?.nodeID)) {
    // Nullify the related KPIDriver fields when node is null
    return {
      [hierarchyAccessor]: null,
      [valueAccessor]: null,
    };
  }

  // Build the actual values for the related KPIDriver fields
  const [hierarchyValue, ...remainingNodeIDParts] = node.nodeID.split('-');
  // rejoin the remaining nodeID parts
  const dimensionValue = remainingNodeIDParts.join('-');

  return {
    [hierarchyAccessor]: hierarchyValue,
    [valueAccessor]: dimensionValue,
  };
};
